// SPDX-FileCopyrightText: 2016-2026 Lari Natri <lari.natri@iki.fi>
// SPDX-License-Identifier: GPL-3.0-or-later

/** Owns one in-memory confirmation across all PDF views in this document lifetime. */
export function createEngagementConfirmation(token: string | undefined): () => void {
  let confirmed = false;
  let pending = false;
  return () => {
    if (!token || confirmed || pending) return;
    // Keep the token only in memory while offline; PDF input never waits for analytics.
    if (typeof navigator !== "undefined" && navigator.onLine === false) return;
    pending = true;
    void (async () => {
      try {
        const response = await fetch("/stats/engagement", {
          method: "POST",
          credentials: "omit",
          cache: "no-store",
          keepalive: true,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        // Invalid/expired tokens cannot recover. Network/server failures retry on a later interaction.
        confirmed = response.ok || (response.status >= 400 && response.status < 500);
      } catch {
        // Analytics is best-effort: synchronous throws and rejected sends are always silent.
      } finally {
        pending = false;
      }
    })();
  };
}

/** Qualifies deliberate trusted input, not rendering, restored pages, or programmatic changes. */
export function isQualifyingPdfInteraction(event: Event): boolean {
  if (!event.isTrusted) return false;
  if (event.type === "pointerdown") return (event as PointerEvent).button === 0;
  if (event.type === "wheel") {
    const wheel = event as WheelEvent;
    return wheel.deltaX !== 0 || wheel.deltaY !== 0;
  }
  if (event.type === "keydown") {
    const key = event as KeyboardEvent;
    return !key.repeat && !["Tab", "Shift", "Control", "Alt", "Meta", "Escape"].includes(key.key);
  }
  return false;
}
